import React from 'react'
import { Table, Button, Form, Card, OverlayTrigger, Tooltip, ButtonProps } from 'react-bootstrap'
import { User } from '../../../types/models/user'
import styled from 'styled-components'

const CreateButton = styled.button<ButtonProps>`
  background: linear-gradient(45deg, #6037a9, #2979ff);
  border: none;
  color: white;
  font-weight: 600;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(41, 121, 255, 0.4);
    background: linear-gradient(45deg, #7646cc, #458fff);
    color: white;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  i {
    font-size: 0.9em;
  }
`

interface UsersTableProps {
  users: User[]
  selectedUsers: string[]
  sortConfig: {
    key: string
    direction: 'ascending' | 'descending' | null
  }
  onSort: (key: string) => void
  onToggleUserSelection: (userId: string) => void
  onToggleAllUsers: () => void
  onEditUser: (user: User) => void
  onDeleteUser: (user: User) => void
  onBulkDelete: () => void
  onCreateUser: () => void
  formatDate: (dateString: string) => string
  gradient: string
  organizations: { id: string; name: string }[]
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  selectedUsers,
  sortConfig,
  onSort,
  onToggleUserSelection,
  onToggleAllUsers,
  onEditUser,
  onDeleteUser,
  onBulkDelete,
  onCreateUser,
  formatDate,
  gradient,
  organizations,
}) => {
  const getUserStatus = (user: User) => {
    const lastLogin = new Date(user.last_login || '')
    const createdAt = new Date(user.created_at)
    const now = new Date()

    // Check if user has never logged in (new user)
    if (lastLogin.getFullYear() < 2000) {
      const daysSinceCreation = Math.floor(
        (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
      )
      if (daysSinceCreation >= 30) {
        return { status: 'danger', message: 'Never logged in' }
      }
      return { status: 'new', message: 'New User' }
    }

    // Check if user is inactive (hasn't logged in for 14-30 days)
    const daysSinceLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24))
    if (daysSinceLogin >= 30) {
      return { status: 'danger', message: formatDate(user.last_login || '') }
    } else if (daysSinceLogin >= 14) {
      return { status: 'warning', message: formatDate(user.last_login || '') }
    }

    return { status: 'active', message: formatDate(user.last_login || '') }
  }

  const getSortedUsers = () => {
    if (!users) return []

    const regularUsers = [...users.filter((user) => user.username !== 'admin')]

    if (!sortConfig.direction) return regularUsers

    return regularUsers.sort((a, b) => {
      let aValue: string | number | Date
      let bValue: string | number | Date

      switch (sortConfig.key) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`
          bValue = `${b.first_name} ${b.last_name}`
          break
        case 'email':
          aValue = a.email
          bValue = b.email
          break
        case 'username':
          aValue = a.username
          bValue = b.username
          break
        case 'created_at':
          aValue = new Date(a.created_at)
          bValue = new Date(b.created_at)
          break
        case 'last_login':
          aValue = new Date(a.last_login || '')
          bValue = new Date(b.last_login || '')
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1
      return 0
    })
  }

  // Helper to map org_id to org name
  const orgIdToName = React.useMemo(() => {
    const map: Record<string, string> = {}
    organizations.forEach((org) => {
      map[org.id] = org.name
    })
    return map
  }, [organizations])

  return (
    <Card bg="dark" text="white" className="mb-4">
      <Card.Header
        className="bg-success d-flex justify-content-between align-items-center"
        style={{ background: gradient }}
      >
        <h4 className="mb-0">User Management</h4>
        <div className="d-flex gap-2">
          {selectedUsers.length > 0 && (
            <Button variant="danger" className="fw-bold" onClick={onBulkDelete}>
              <i className="fas fa-trash-alt me-2"></i>Delete Selected ({selectedUsers.length})
            </Button>
          )}
          <CreateButton onClick={onCreateUser}>
            <i className="fas fa-plus me-2"></i>Create New User
          </CreateButton>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="table-responsive">
          <Table striped bordered hover variant="dark" className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Form.Check
                    type="checkbox"
                    onChange={onToggleAllUsers}
                    checked={
                      selectedUsers.length ===
                      (users.filter((user) => user.username !== 'admin').length || 0)
                    }
                  />
                </th>
                <th
                  onClick={() => onSort('name')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Name{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'name'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1 text-${sortConfig.key === 'name' ? 'primary' : 'secondary'}`}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('email')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Email{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'email'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1 text-${sortConfig.key === 'email' ? 'primary' : 'secondary'}`}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('username')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Username{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'username'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1 text-${sortConfig.key === 'username' ? 'primary' : 'secondary'}`}
                  ></i>
                </th>
                <th>Role</th>
                <th>Organization</th>
                <th
                  onClick={() => onSort('org_id')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Created At{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'created_at'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1 text-${sortConfig.key === 'created_at' ? 'primary' : 'secondary'}`}
                  ></i>
                </th>
                <th
                  onClick={() => onSort('last_login')}
                  style={{ cursor: 'pointer' }}
                  className="text-nowrap"
                >
                  Last Login{' '}
                  <i
                    className={`fas fa-sort${
                      sortConfig.key === 'last_login'
                        ? sortConfig.direction === 'ascending'
                          ? '-up'
                          : '-down'
                        : ''
                    } ms-1 text-${sortConfig.key === 'last_login' ? 'primary' : 'secondary'}`}
                  ></i>
                </th>
                <th style={{ width: '200px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getSortedUsers().map((user) => {
                const status = getUserStatus(user)
                return (
                  <tr key={user.id}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => onToggleUserSelection(user.id)}
                      />
                    </td>
                    <td>{`${user.first_name} ${user.last_name}`}</td>
                    <td>{user.email}</td>
                    <td>{user.username}</td>
                    <td>
                      <span className="badge bg-secondary">User</span>
                    </td>
                    <td>{orgIdToName[user.org_id] || <span className="text-muted">N/A</span>}</td>
                    <td>{formatDate(user.created_at)}</td>
                    <td>
                      {(() => {
                        if (status.status === 'new') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>
                                  Created: {formatDate(user.created_at)}
                                </Tooltip>
                              }
                            >
                              <div className="badge bg-info text-dark" style={{ cursor: 'help' }}>
                                <i className="fas fa-star me-1"></i>
                                New User
                              </div>
                            </OverlayTrigger>
                          )
                        } else if (status.status === 'warning') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>
                                  Last login: {status.message}
                                </Tooltip>
                              }
                            >
                              <div
                                className="badge bg-warning text-dark"
                                style={{ cursor: 'help' }}
                              >
                                <i className="fas fa-clock me-1"></i>
                                Inactive
                              </div>
                            </OverlayTrigger>
                          )
                        } else if (status.status === 'danger') {
                          return (
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${user.id}`}>{status.message}</Tooltip>
                              }
                            >
                              <div
                                className="badge bg-danger text-white"
                                style={{ cursor: 'help' }}
                              >
                                <i className="fas fa-exclamation-circle me-1"></i>
                                Inactive
                              </div>
                            </OverlayTrigger>
                          )
                        } else {
                          return <span>{status.message}</span>
                        }
                      })()}
                    </td>
                    <td>
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onEditUser(user)}
                          style={{ width: '80px' }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDeleteUser(user)}
                          style={{ width: '80px' }}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  )
}

export default UsersTable
